import { apiClient } from '../config';
import { endpoints } from '../endpoints';

/**
 * User service for handling user management operations
 */
export const userService = {
  /**
   * Get all users
   * @returns {Promise<Array>} - Promise resolving to array of users
   */
  getAllUsers: async () => {
    const response = await apiClient.get(endpoints.users.list);
    return response.data;
  },

  /**
   * Get user by ID
   * @param {string} id - User ID
   * @returns {Promise<Object>} - Promise resolving to user data
   */
  getUserById: async (id) => {
    const response = await apiClient.get(endpoints.users.details(id));
    return response.data;
  },

  /**
   * Get current user
   * @returns {Promise<Object>} - Promise resolving to current user data
   */
  getCurrentUser: async () => {
    const response = await apiClient.get(endpoints.users.me);
    return response.data;
  },

  /**
   * Update user
   * @param {string} id - User ID
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>} - Promise resolving to updated user data
   */
  updateUser: async (id, userData) => {
    const response = await apiClient.put(endpoints.users.update(id), userData);
    return response.data;
  },

  /**
   * Update user roles
   * @param {string} id - User ID
   * @param {string[]} roles - New roles array (AUTHOR, REVIEWER, EDITOR, CHIEF_EDITOR, ADMIN)
   * @returns {Promise<Object>} - Promise resolving to updated user data
   */
  updateUserRole: async (id, roles) => {
    const response = await apiClient.put(endpoints.users.updateRole(id), { roles });
    return response.data;
  },

  /**
   * Update user status
   * @param {string} id - User ID
   * @param {string} status - New status (ACTIVE, INACTIVE, SUSPENDED, PENDING_VERIFICATION)
   * @returns {Promise<Object>} - Promise resolving to updated user data
   */
  updateUserStatus: async (id, status) => {
    const response = await apiClient.put(endpoints.users.updateStatus(id), { status });
    return response.data;
  },

  /**
   * Get users by role
   * @param {string} role - Role to filter by (AUTHOR, REVIEWER, EDITOR, CHIEF_EDITOR, ADMIN)
   * @returns {Promise<Array>} - Promise resolving to array of users with the specified role
   */
  getUsersByRole: async (role) => {
    const response = await apiClient.get(endpoints.users.getByRole(role));
    return response.data;
  },

  /**
   * Search users by name, email, or affiliation
   * @param {string} query - Search query
   * @param {string} role - Optional role to filter by
   * @returns {Promise<Array>} - Promise resolving to array of users matching the search criteria
   */
  searchUsers: async (query, role = null) => {
    const params = { q: query };
    if (role) {
      params.role = role;
    }
    const response = await apiClient.get(endpoints.users.search, { params });
    return response.data;
  },

  /**
   * Create a new user
   * @param {Object} userData - User data to create
   * @returns {Promise<Object>} - Promise resolving to created user data
   */
  createUser: async (userData) => {
    const response = await apiClient.post(endpoints.users.list, userData);
    return response.data;
  },

  /**
   * Deactivate a user account (mark as deleted)
   * @param {string} id - User ID
   * @param {string} reason - Reason for deactivation
   * @returns {Promise<Object>} - Promise resolving to deactivated user data
   */
  deleteUser: async (id, reason) => {
    const response = await apiClient.delete(endpoints.users.details(id), {
      data: { reason }
    });
    return response.data;
  },

  /**
   * Get non-admin users for co-author selection
   * @param {string} email - Optional email to filter users by
   * @returns {Promise<Array>} - Promise resolving to array of non-admin users
   */
  getCoauthors: async (email) => {
    const params = email ? { email } : {};
    const response = await apiClient.get(endpoints.users.coauthors, { params });
    return response.data;
  },

  /**
   * Reset user password (Admin/Managing Chief Editor only)
   * @param {string} id - User ID
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Promise resolving to success message
   */
  resetPassword: async (id, newPassword) => {
    const response = await apiClient.put(endpoints.users.resetPassword(id), { newPassword });
    return response.data;
  },

  /**
   * Get all editorial board members
   * @returns {Promise<Array>} - Promise resolving to array of editorial board members
   */
  getEditorialBoardMembers: async () => {
    const response = await apiClient.get(endpoints.users.editorialBoard);
    return response.data;
  }
};
